import {
  AutopilotTaskDto,
  TaskCategoryWorkflowType,
  TaskCategoryStage,
} from '@urbanx/agx.activities.client'
import {
  UploadedDocumentInfoDocumentType,
  UploadedDocumentInfoSigningMethod,
  useStaffGetDocumentsForCampaign,
} from '@urbanx/agx.campaigns.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import { cleanFullAddress } from '@urbanx/agx.ui-components2'
import { MenuTitles } from 'constants/menu'
import { useSelectedAgency, useSelectedCampaign } from 'hooks'
import { ContentLayout, PageLayout } from 'layout'
import { useCallback, useContext, useMemo, useState } from 'react'
import AgreementCompliance from './AgreementCompliance/AgreementCompliance'
import Tasks, { TaskTabs } from 'components/tasks/Tasks'
import TaskDueDateBadge from 'components/tasks/TaskDueDateBadge'
import { getStepsCompletionInfo } from 'utils/tasks'
import WithdrawListingAction from '../WithdrawListingAction'
import TaskCompletionConfirmationModal from 'pages/allTasks/TaskModal/TaskCompletionConfirmationModal'

const CampaignTasks = () => {
  const { userAccount } = useContext(AuthContext)
  const { agency } = useSelectedAgency()
  const { campaign } = useSelectedCampaign()

  const [showComplianceConfirmation, setShowComplianceConfirmation] =
    useState(false)
  const [allTasks, setAllTasks] = useState<AutopilotTaskDto[]>([])

  const { data: documentGroups, isPending: documentsPending } =
    useStaffGetDocumentsForCampaign(
      campaign?.campaignId || '',
      !!campaign?.campaignId
    )

  const agencyAgreementSignedByAgent = useMemo(() => {
    return documentGroups
      ?.flatMap((group) => group.documents ?? [])
      .find(
        (doc) =>
          doc.documentType ===
            UploadedDocumentInfoDocumentType.AgencyAgreement &&
          doc.signingMethod === UploadedDocumentInfoSigningMethod.Agent
      )
  }, [documentGroups])

  const complianceTasks = useMemo(() => {
    return allTasks.filter(
      (t) =>
        !t.isLocked &&
        t.category?.workflowType === TaskCategoryWorkflowType.Campaign &&
        t.category?.stage === TaskCategoryStage.AgreementCompliance
    )
  }, [allTasks])
  const {
    allStepsCompleted: allComplianceStepsCompleted,
    latestCompletedDate,
  } = useMemo(() => getStepsCompletionInfo(complianceTasks), [complianceTasks])

  const handleTasksChange = useCallback((tasks: AutopilotTaskDto[]) => {
    setAllTasks(tasks)
  }, [])

  // Filter out AgreementCompliance tasks from main Tasks list (they're rendered separately)
  const taskFilter = useCallback(
    (task: AutopilotTaskDto) =>
      task.category?.stage !== TaskCategoryStage.AgreementCompliance,
    []
  )

  const renderAdditionalContent = useCallback(
    ({
      selectedTab,
      selectedWorkflowStage,
      allTasksCompleted,
      isViewingCompletedStage,
    }: {
      selectedTab: TaskTabs
      selectedWorkflowStage?: TaskCategoryStage
      allTasksCompleted: boolean
      isViewingCompletedStage: boolean
    }) => {
      const shouldShowCompliance =
        selectedWorkflowStage === TaskCategoryStage.AgreementCompliance ||
        (!allComplianceStepsCompleted &&
          !documentsPending &&
          selectedTab === TaskTabs.Tasks &&
          agencyAgreementSignedByAgent &&
          complianceTasks.length > 0)

      // Don't show compliance when viewing a different completed stage
      if (
        isViewingCompletedStage &&
        selectedWorkflowStage !== TaskCategoryStage.AgreementCompliance
      )
        return null
      // Don't show when all completed and not viewing a specific stage
      if (allTasksCompleted && !isViewingCompletedStage) return null
      if (!shouldShowCompliance) return null

      const dueDate =
        complianceTasks
          .flatMap((task) => {
            const dateValue = allComplianceStepsCompleted
              ? latestCompletedDate
              : task.dueUtc

            return dateValue ? [new Date(dateValue)] : []
          })
          .sort((a, b) => a.getTime() - b.getTime())[0] ?? ''

      return (
        <AgreementCompliance
          badge={
            <TaskDueDateBadge
              dueDate={dueDate}
              isCompleted={allComplianceStepsCompleted}
              completedDate={latestCompletedDate}
            />
          }
          tasks={complianceTasks}
          onCompleteSuccess={() => setShowComplianceConfirmation(true)}
        />
      )
    },
    [
      allComplianceStepsCompleted,
      documentsPending,
      agencyAgreementSignedByAgent,
      complianceTasks,
      latestCompletedDate,
    ]
  )

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CAMPAIGN_TASKS}
        isCampaigns={true}
        secondaryActions={<WithdrawListingAction />}
      >
        <Tasks
          campaignId={campaign?.campaignId ?? ''}
          primaryWorkflowType={TaskCategoryWorkflowType.Campaign}
          taskFilter={taskFilter}
          onTasksChange={handleTasksChange}
          renderAdditionalContent={renderAdditionalContent}
        />

        <TaskCompletionConfirmationModal
          isOpen={showComplianceConfirmation}
          onOpenChange={setShowComplianceConfirmation}
          title="Agreement Compliance now complete"
          message="Campaign will progress to New Listing"
        />
      </ContentLayout>
    </PageLayout>
  )
}

export default CampaignTasks
